import { Temporal } from 'temporal-polyfill';
import { expandDates } from './recurrence';
import { escapeText, serializeLines } from './icsLines';
import { buildVTimeZone, formatLocalDateTime, icalWeekday } from './vtimezone';
import type {
  ClassSeries,
  Location,
  OccurrenceOverride,
  RecurrenceRule,
  WallClockTime,
} from './types';

export const PROD_ID = '-//yoga-scheduler//Phase 1//EN';

/** Horizon used to find overrides and DTSTART for open-ended series. */
const OPEN_ENDED_YEARS = 5;

export interface IcsRequest {
  readonly series: readonly ClassSeries[];
  readonly locations: readonly Location[];
  readonly overrides?: readonly OccurrenceOverride[];
  /** DTSTAMP for every event; pass a fixed instant for reproducible output. */
  readonly now: Temporal.Instant;
  readonly calendarName?: string;
  /**
   * Minutes before each class that the calendar app raises a reminder
   * (VALARM). Defaults to 15; null disables reminders entirely.
   */
  readonly reminderMinutesBefore?: number | null;
}

interface SeriesContext {
  readonly series: ClassSeries;
  readonly location: Location;
  readonly firstDate: Temporal.PlainDate;
  readonly lastDate: Temporal.PlainDate;
}

function pad(value: number, width = 2): string {
  return String(value).padStart(width, '0');
}

/** UTC instant in iCalendar basic format, e.g. `20260727T190000Z`. */
export function formatUtc(instant: Temporal.Instant): string {
  const utc = instant.toZonedDateTimeISO('UTC');
  return `${formatLocalDateTime(utc.toPlainDateTime())}Z`;
}

export function formatDuration(minutes: number): string {
  if (minutes <= 0) {
    return 'PT0S';
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  if (hours === 0) {
    return `PT${rest}M`;
  }
  return rest === 0 ? `PT${hours}H` : `PT${hours}H${rest}M`;
}

function localDateTime(date: Temporal.PlainDate, time: WallClockTime): string {
  const plainTime = Temporal.PlainTime.from(time);
  return `${pad(date.year, 4)}${pad(date.month)}${pad(date.day)}T${pad(plainTime.hour)}${pad(
    plainTime.minute,
  )}${pad(plainTime.second)}`;
}

/**
 * The recurrence rule as an RRULE value, or null for a one-off.
 *
 * `biweekly` becomes `FREQ=WEEKLY;INTERVAL=2`, which is how iCalendar expresses
 * it; the app keeps them as separate variants only because the UI does.
 */
export function toRruleValue(rule: RecurrenceRule): string | null {
  switch (rule.freq) {
    case 'once':
      return null;
    case 'daily':
      return 'FREQ=DAILY';
    case 'weekly':
      return `FREQ=WEEKLY;BYDAY=${rule.byWeekday.map(icalWeekday).join(',')}`;
    case 'biweekly':
      return `FREQ=WEEKLY;INTERVAL=2;BYDAY=${rule.byWeekday.map(icalWeekday).join(',')}`;
    case 'monthly':
      return rule.mode === 'byMonthDay'
        ? `FREQ=MONTHLY;BYMONTHDAY=${rule.monthDay}`
        : `FREQ=MONTHLY;BYDAY=${rule.nth}${icalWeekday(rule.weekday)}`;
    default: {
      const exhaustive: never = rule;
      throw new Error(`Unsupported rule: ${JSON.stringify(exhaustive)}`);
    }
  }
}

function contextFor(
  series: ClassSeries,
  locationsById: ReadonlyMap<string, Location>,
): SeriesContext | null {
  const location = locationsById.get(series.locationId);
  if (location === undefined) {
    return null;
  }

  const searchStart = Temporal.PlainDate.from(series.startDate);
  const searchEnd =
    series.endDate === null
      ? searchStart.add({ years: OPEN_ENDED_YEARS })
      : Temporal.PlainDate.from(series.endDate);

  // DTSTART must be a real occurrence: for "weekly on Tuesday" starting on a
  // Monday, the first event is the Tuesday, not the series' start date.
  const dates = expandDates({
    series,
    timeZone: location.timeZone,
    rangeStart: searchStart,
    rangeEnd: searchEnd,
  });
  const firstDate = dates[0];
  if (firstDate === undefined) {
    return null;
  }

  return { series, location, firstDate, lastDate: searchEnd };
}

/**
 * A display reminder relative to the event start (RFC 5545 §3.6.6).
 *
 * This is what turns "send my schedule to my calendar" into "my phone
 * notifies me before class": the calendar app's own notification machinery
 * fires it — reliably, offline, with no server. The web platform cannot do
 * this itself: scheduled notifications for a closed app require either a push
 * server (rejected: this app is serverless and offline) or the abandoned
 * Notification Triggers API. See docs/architecture.md.
 */
function alarmLines(title: string, minutesBefore: number): string[] {
  return [
    'BEGIN:VALARM',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapeText(`${title} starts in ${minutesBefore} minutes`)}`,
    `TRIGGER:-PT${minutesBefore}M`,
    'END:VALARM',
  ];
}

function eventLines(context: SeriesContext, request: IcsRequest): string[] {
  const { series, location } = context;
  const tzid = location.timeZone;
  const uid = `${series.id}@yoga-scheduler`;
  const dtstamp = formatUtc(request.now);
  const reminder = request.reminderMinutesBefore === undefined ? 15 : request.reminderMinutesBefore;

  const lines = [
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=${tzid}:${localDateTime(context.firstDate, series.startTime)}`,
    `DURATION:${formatDuration(series.durationMinutes)}`,
    `SUMMARY:${escapeText(series.title)}`,
  ];

  const rrule = toRruleValue(series.recurrence);
  if (rrule !== null) {
    // UNTIL must be UTC when DTSTART carries a TZID (RFC 5545 §3.3.10). It is the
    // last instant an occurrence may START, so the final class is included.
    const until =
      series.endDate === null
        ? null
        : Temporal.PlainDate.from(series.endDate)
            .toZonedDateTime({
              timeZone: tzid,
              plainTime: Temporal.PlainTime.from(series.startTime),
            })
            .toInstant();
    lines.push(`RRULE:${rrule}${until === null ? '' : `;UNTIL=${formatUtc(until)}`}`);
  }

  const locationText = [location.name, location.address]
    .filter((part) => part.length > 0)
    .join(', ');
  if (locationText.length > 0) {
    lines.push(`LOCATION:${escapeText(locationText)}`);
  }
  if (series.notes.length > 0) {
    lines.push(`DESCRIPTION:${escapeText(series.notes)}`);
  }

  const overrides = (request.overrides ?? []).filter(
    (override) => override.deletedAt === null && override.seriesId === series.id,
  );

  // Cancellations collapse into a single EXDATE line for the series.
  const cancelled = overrides
    .filter((override) => override.status === 'cancelled')
    .map((override) =>
      localDateTime(Temporal.PlainDate.from(override.originalDate), series.startTime),
    )
    .sort();
  if (cancelled.length > 0) {
    lines.push(`EXDATE;TZID=${tzid}:${cancelled.join(',')}`);
  }

  if (reminder !== null) {
    lines.push(...alarmLines(series.title, reminder));
  }

  lines.push('END:VEVENT');

  // Each move becomes a companion VEVENT sharing the UID, identified by the
  // original start time. This is how iCalendar models "this one instance moved".
  const moved = overrides
    .filter((override) => override.status === 'moved')
    .sort((a, b) => a.originalDate.localeCompare(b.originalDate));

  for (const override of moved) {
    const targetLocation =
      override.newLocationId === null
        ? location
        : (request.locations.find((candidate) => candidate.id === override.newLocationId) ??
          location);
    const targetDate = Temporal.PlainDate.from(override.newDate ?? override.originalDate);
    const targetTime = override.newStartTime ?? series.startTime;
    const targetTzid = targetLocation.timeZone;
    const targetText = [targetLocation.name, targetLocation.address]
      .filter((part) => part.length > 0)
      .join(', ');

    lines.push(
      'BEGIN:VEVENT',
      `UID:${uid}`,
      `DTSTAMP:${dtstamp}`,
      `RECURRENCE-ID;TZID=${tzid}:${localDateTime(
        Temporal.PlainDate.from(override.originalDate),
        series.startTime,
      )}`,
      `DTSTART;TZID=${targetTzid}:${localDateTime(targetDate, targetTime)}`,
      `DURATION:${formatDuration(series.durationMinutes)}`,
      `SUMMARY:${escapeText(series.title)}`,
    );
    if (targetText.length > 0) {
      lines.push(`LOCATION:${escapeText(targetText)}`);
    }
    if (series.notes.length > 0) {
      lines.push(`DESCRIPTION:${escapeText(series.notes)}`);
    }
    if (reminder !== null) {
      lines.push(...alarmLines(series.title, reminder));
    }
    lines.push('END:VEVENT');
  }

  return lines;
}

/**
 * A complete VCALENDAR document.
 *
 * The result is a SNAPSHOT: importing it copies the schedule as it stands, and
 * later edits will not propagate. Live updates need a subscribable feed, which
 * requires a server (Phase 2).
 */
export function buildIcs(request: IcsRequest): string {
  const locationsById = new Map(
    request.locations.filter((location) => location.deletedAt === null).map((l) => [l.id, l]),
  );

  const contexts = request.series
    .filter((series) => series.deletedAt === null)
    .map((series) => contextFor(series, locationsById))
    .filter((context): context is SeriesContext => context !== null);

  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', `PRODID:${PROD_ID}`, 'CALSCALE:GREGORIAN'];
  if (request.calendarName !== undefined) {
    lines.push(`X-WR-CALNAME:${escapeText(request.calendarName)}`);
  }

  // One VTIMEZONE per distinct zone, spanning every series that uses it.
  const zones = new Map<string, { from: Temporal.PlainDate; to: Temporal.PlainDate }>();
  for (const context of contexts) {
    const zone = context.location.timeZone;
    const existing = zones.get(zone);
    if (existing === undefined) {
      zones.set(zone, { from: context.firstDate, to: context.lastDate });
    } else {
      zones.set(zone, {
        from:
          Temporal.PlainDate.compare(context.firstDate, existing.from) < 0
            ? context.firstDate
            : existing.from,
        to:
          Temporal.PlainDate.compare(context.lastDate, existing.to) > 0
            ? context.lastDate
            : existing.to,
      });
    }
  }

  for (const [timeZone, span] of [...zones.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    lines.push(...buildVTimeZone({ timeZone, from: span.from, to: span.to }));
  }

  for (const context of contexts) {
    lines.push(...eventLines(context, request));
  }

  lines.push('END:VCALENDAR');
  return serializeLines(lines);
}
